/**
 * useProcurementAutoSave
 * Debounced, per-row auto-save for the procurement planner.
 *
 * Contract:
 * - `scheduleRowSave(itemId)` starts/restarts the row's debounce timer;
 *   `flushRowSave(itemId)` cancels it and saves on the next tick (so state
 *   updates from the same event handler have committed before the draft is
 *   re-read). Both are no-ops while `enabled` is false.
 * - A save re-reads the latest draft for the row from `displayItems`, diffs it
 *   with `buildRowSaveOperations`, and fires only the API calls that row needs
 *   (inventory as a single-entry `saveInventory` call). Nothing changed = no call.
 * - Saves are serialized per row: a request while one is in flight queues at
 *   most one follow-up that runs afterwards with the then-latest draft.
 * - `rowStatus[itemId]` is `saving` during the request, `saved` on success
 *   (auto-clears after `savedDisplayMs`), or `error` with the API message.
 *   On success the `onItemSaved` / `onInventorySaved` / `onManualDeleted`
 *   callbacks let the page patch its state in place instead of reloading.
 *   `onInventorySaved` also receives the ingredient ids that request actually
 *   sent, since the bulk response lists every inventory row of the list.
 * - A created manual row keeps working under its temp (negative) id: once the
 *   create resolves, timers and queued follow-ups keyed by the temp id act on
 *   the server id, even before the page has re-rendered with it.
 * - `flushAll()` cancels all timers, saves every scheduled row, waits for every
 *   in-flight save (and queued follow-ups) and reports which rows saved/failed.
 * - All timers are cleared on unmount and when `procurementListId` changes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addManualProcurementItem,
  deleteProcurementItem,
  saveInventory,
  updateProcurementItem,
} from '@/lib/api/procurement';
import type { BulkInventoryResponse, ProcurementItem } from '@/types/api';
import type { QuantitySaveState } from '@/components/admin/QuantitySaveIndicator';
import { buildRowSaveOperations, hasRowSaveOperations } from './helpers';
import type { ProcurementDisplayItem } from './types';

export const DEFAULT_AUTO_SAVE_DEBOUNCE_MS = 1500;
export const DEFAULT_SAVED_STATUS_MS = 2000;

export type FlushResult = {
  /** Row ids (as passed to scheduleRowSave) whose save succeeded */
  saved: number[];
  /** Row ids whose save failed or could not be built (kept in error status) */
  failed: number[];
};

export type ProcurementAutoSaveOptions = {
  procurementListId: number | null;
  displayItems: ProcurementDisplayItem[];
  enabled: boolean;
  debounceMs?: number;
  savedDisplayMs?: number;
  onItemSaved: (previousId: number, saved: ProcurementItem) => void;
  onInventorySaved: (entries: BulkInventoryResponse['entries'], savedIngredientIds: number[]) => void;
  onManualDeleted: (itemId: number) => void;
};

export type ProcurementAutoSaveResult = {
  rowStatus: Record<number, QuantitySaveState>;
  scheduleRowSave: (itemId: number) => void;
  flushRowSave: (itemId: number) => void;
  flushAll: () => Promise<FlushResult>;
  hasPendingSaves: boolean;
};

type SaveOutcome = 'saved' | 'failed' | 'noop' | 'skipped';
type Timer = ReturnType<typeof setTimeout>;

export function useProcurementAutoSave({
  procurementListId,
  displayItems,
  enabled,
  debounceMs = DEFAULT_AUTO_SAVE_DEBOUNCE_MS,
  savedDisplayMs = DEFAULT_SAVED_STATUS_MS,
  onItemSaved,
  onInventorySaved,
  onManualDeleted,
}: ProcurementAutoSaveOptions): ProcurementAutoSaveResult {
  const [rowStatus, setRowStatus] = useState<Record<number, QuantitySaveState>>({});
  const [pendingRowCount, setPendingRowCount] = useState(0);

  // Refs so timer/promise callbacks always read the latest props (no stale closures).
  const displayItemsRef = useRef(displayItems);
  displayItemsRef.current = displayItems;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const listIdRef = useRef(procurementListId);
  listIdRef.current = procurementListId;
  const callbacksRef = useRef({ onItemSaved, onInventorySaved, onManualDeleted });
  callbacksRef.current = { onItemSaved, onInventorySaved, onManualDeleted };

  const debounceTimersRef = useRef(new Map<number, Timer>());
  const savedTimersRef = useRef(new Map<number, Timer>());
  const inFlightRef = useRef(new Map<number, Promise<SaveOutcome>>());
  const queuedRef = useRef(new Set<number>());
  const mountedRef = useRef(true);
  // Created manual rows: temp id -> server id, plus the created item for the
  // window before the page has re-rendered the row under its server id.
  const idAliasRef = useRef(new Map<number, number>());
  const createdItemsRef = useRef(new Map<number, ProcurementItem>());

  const resolveId = useCallback((itemId: number) => idAliasRef.current.get(itemId) ?? itemId, []);

  const findEntry = useCallback((itemId: number, rowId: number): ProcurementDisplayItem | undefined => {
    const items = displayItemsRef.current;
    const direct = items.find((candidate) => candidate.item.id === rowId);
    if (direct || rowId === itemId) return direct;
    // The page still lists the row under its temp id: save the draft it shows
    // against the item the server created.
    const created = createdItemsRef.current.get(rowId);
    const stale = items.find((candidate) => candidate.item.id === itemId);
    return created && stale ? { item: created, draft: stale.draft, isLocalOnly: false } : undefined;
  }, []);

  const syncPending = useCallback(() => {
    if (!mountedRef.current) return;
    setPendingRowCount(debounceTimersRef.current.size + inFlightRef.current.size);
  }, []);

  const setStatus = useCallback((itemId: number, state: QuantitySaveState | null) => {
    if (!mountedRef.current) return;
    setRowStatus((current) => {
      if (state === null) {
        if (!(itemId in current)) return current;
        const { [itemId]: _, ...rest } = current;
        return rest;
      }
      return { ...current, [itemId]: state };
    });
  }, []);

  const scheduleSavedClear = useCallback((itemId: number) => {
    const existing = savedTimersRef.current.get(itemId);
    if (existing) clearTimeout(existing);
    savedTimersRef.current.set(itemId, setTimeout(() => {
      savedTimersRef.current.delete(itemId);
      if (!mountedRef.current) return;
      setRowStatus((current) => {
        if (current[itemId]?.status !== 'saved') return current;
        const { [itemId]: _, ...rest } = current;
        return rest;
      });
    }, savedDisplayMs));
  }, [savedDisplayMs]);

  const runSave = useCallback(async (itemId: number): Promise<SaveOutcome> => {
    const listId = listIdRef.current;
    if (listId === null || !enabledRef.current) return 'skipped';

    const rowId = resolveId(itemId);
    const entry = findEntry(itemId, rowId);
    if (!entry) return 'skipped';

    const ops = buildRowSaveOperations(entry.item, entry.draft, entry.isLocalOnly);
    if (ops.error) {
      setStatus(rowId, { status: 'error', message: ops.error });
      return 'failed';
    }
    if (!hasRowSaveOperations(ops)) {
      // Draft matches the server again (e.g. an edit was reverted): drop a stale error.
      setStatus(rowId, null);
      return 'noop';
    }

    const isCurrentList = () => listIdRef.current === listId;
    const callbacks = callbacksRef.current;
    let statusId = rowId;
    setStatus(rowId, { status: 'saving' });

    try {
      const tasks: Promise<void>[] = [];
      if (ops.deleteManual) {
        tasks.push(deleteProcurementItem(rowId).then(() => {
          if (isCurrentList()) callbacks.onManualDeleted(rowId);
        }));
      }
      if (ops.createManual) {
        tasks.push(addManualProcurementItem(listId, ops.createManual).then((saved) => {
          statusId = saved.id;
          if (!isCurrentList()) return;
          // Record the remap before the page hears about it, so anything still
          // keyed by the temp id (timer, queued follow-up) lands on the server row.
          idAliasRef.current.set(rowId, saved.id);
          createdItemsRef.current.set(saved.id, saved);
          callbacks.onItemSaved(rowId, saved);
        }));
      }
      if (ops.itemPayload) {
        tasks.push(updateProcurementItem(rowId, ops.itemPayload).then((saved) => {
          if (!isCurrentList()) return;
          if (createdItemsRef.current.has(rowId)) createdItemsRef.current.set(rowId, saved);
          callbacks.onItemSaved(rowId, saved);
        }));
      }
      const inventoryEntry = ops.inventoryEntry;
      if (inventoryEntry) {
        tasks.push(saveInventory(listId, [inventoryEntry]).then((response) => {
          if (isCurrentList()) callbacks.onInventorySaved(response.entries, [inventoryEntry.ingredientId]);
        }));
      }
      // Wait for every request before releasing the row: a rejection must not
      // let a queued follow-up start while a sibling request is still in flight
      // (its late response could otherwise land after the newer one).
      const settled = await Promise.allSettled(tasks);
      const rejected = settled.find((outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected');
      if (rejected) throw rejected.reason;

      if (!isCurrentList()) return 'saved';
      if (ops.deleteManual) {
        setStatus(rowId, null);
        return 'saved';
      }
      if (statusId !== rowId) setStatus(rowId, null);
      setStatus(statusId, { status: 'saved' });
      scheduleSavedClear(statusId);
      return 'saved';
    } catch (error) {
      if (isCurrentList()) {
        setStatus(rowId, {
          status: 'error',
          message: error instanceof Error ? error.message : 'Failed to save procurement change',
        });
      }
      return 'failed';
    }
  }, [findEntry, resolveId, scheduleSavedClear, setStatus]);

  // Serialize per row: one save in flight, at most one queued follow-up (latest draft wins).
  // Keyed by the resolved (server) id so a temp id and its server id share one slot.
  const requestSave = useCallback((itemId: number): Promise<SaveOutcome> => {
    const rowId = resolveId(itemId);
    const inFlight = inFlightRef.current.get(rowId);
    if (inFlight) {
      queuedRef.current.add(rowId);
      return inFlight;
    }

    const promise = runSave(itemId)
      .catch((): SaveOutcome => 'failed')
      .then(async (outcome) => {
        inFlightRef.current.delete(rowId);
        if (queuedRef.current.delete(rowId)) {
          // Let the page commit what the save callbacks patched (reconciled
          // draft, temp -> server id remap) before the follow-up re-reads it.
          await new Promise((resolve) => setTimeout(resolve, 0));
          return requestSave(itemId);
        }
        syncPending();
        return outcome;
      });

    inFlightRef.current.set(rowId, promise);
    syncPending();
    return promise;
  }, [resolveId, runSave, syncPending]);

  const clearDebounce = useCallback((itemId: number) => {
    const existing = debounceTimersRef.current.get(itemId);
    if (existing) {
      clearTimeout(existing);
      debounceTimersRef.current.delete(itemId);
    }
  }, []);

  const scheduleWithDelay = useCallback((itemId: number, delayMs: number) => {
    if (!enabledRef.current || listIdRef.current === null) return;
    const rowId = resolveId(itemId);
    clearDebounce(rowId);
    debounceTimersRef.current.set(rowId, setTimeout(() => {
      debounceTimersRef.current.delete(rowId);
      void requestSave(itemId);
    }, delayMs));
    syncPending();
  }, [clearDebounce, requestSave, resolveId, syncPending]);

  const scheduleRowSave = useCallback((itemId: number) => {
    scheduleWithDelay(itemId, debounceMs);
  }, [debounceMs, scheduleWithDelay]);

  // Defer one tick so a state update issued in the same handler (exclusion
  // toggle, vendor change) has committed before the draft is re-read.
  const flushRowSave = useCallback((itemId: number) => {
    scheduleWithDelay(itemId, 0);
  }, [scheduleWithDelay]);

  const flushAll = useCallback(async (): Promise<FlushResult> => {
    const pending = new Map<number, Promise<SaveOutcome>>(inFlightRef.current);
    const scheduled = [...debounceTimersRef.current.keys()];
    for (const timer of debounceTimersRef.current.values()) clearTimeout(timer);
    debounceTimersRef.current.clear();

    if (enabledRef.current && listIdRef.current !== null) {
      for (const itemId of scheduled) {
        pending.set(itemId, requestSave(itemId));
      }
    }

    const outcomes = new Map<number, SaveOutcome>();
    await Promise.all([...pending.entries()].map(async ([itemId, promise]) => {
      outcomes.set(itemId, await promise);
    }));
    // Drain anything that started while we were waiting.
    while (inFlightRef.current.size > 0) {
      await Promise.all([...inFlightRef.current.values()]);
    }
    syncPending();

    const result: FlushResult = { saved: [], failed: [] };
    for (const [itemId, outcome] of outcomes) {
      if (outcome === 'saved') result.saved.push(itemId);
      else if (outcome === 'failed') result.failed.push(itemId);
    }
    return result;
  }, [requestSave, syncPending]);

  // Reset timers and statuses when the list changes; clear everything on unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      for (const timer of debounceTimersRef.current.values()) clearTimeout(timer);
      debounceTimersRef.current.clear();
      for (const timer of savedTimersRef.current.values()) clearTimeout(timer);
      savedTimersRef.current.clear();
      queuedRef.current.clear();
      idAliasRef.current.clear();
      createdItemsRef.current.clear();
    };
  }, [procurementListId]);

  useEffect(() => {
    setRowStatus({});
    setPendingRowCount(0);
  }, [procurementListId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    rowStatus,
    scheduleRowSave,
    flushRowSave,
    flushAll,
    hasPendingSaves: pendingRowCount > 0,
  };
}
